import type { FC } from "react";

import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";
import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import { getProductDisplay } from "@locators/components/shared/utils/shopProductTypes";

import { useConfig } from "@jti/app-context/useConfig";
import type { StoreLocatorProductAttribute } from "@jti/fe-common";

import { StoreLocatorProductAttributeValues } from "./components/StoreLocatorProductAttributeValues";
import { StoreLocatorStickAttributeValues } from "./components/StoreLocatorStickAttributeValues";

import { createMergedStickAttribute, getProductCategoryByVersion } from "./utils";

import styles from "./StoreLocatorAvailableProducts.module.scss";

type StoreLocatorAvailableProductsProps = {
  isListView: boolean;
  productAttributes: StoreLocatorProductAttribute[];
};

export const StoreLocatorAvailableProducts: FC<StoreLocatorAvailableProductsProps> = ({
  productAttributes,
  isListView,
}) => {
  const { storeLocatorLayoutVersion = "v1" } = useConfig();
  const { __ } = useLocatorTranslations();
  const { isAvailableDevicesEnabled, isAvailableSticksEnabled, isMultiBrandSticksEnabled } = useStoreLocatorContext();

  const checkIsAttributeAvailable = (category: string) => {
    const productCategory = getProductCategoryByVersion(storeLocatorLayoutVersion);
    return isAvailableDevicesEnabled && category === productCategory;
  };

  const checkIsStickAttributeAvailable = (category: string) => {
    return isAvailableSticksEnabled && category === "Stick";
  };

  const translateProductTypes = (values: { value: string | number }[]) =>
    values.map(({ value }) => {
      const display = getProductDisplay(value.toString());
      return { value: display ? __(display.i18nKey) : value };
    });

  const attributes = productAttributes.filter(({ category }) => checkIsAttributeAvailable(category));
  const stickAttributes = productAttributes.filter(({ category }) => checkIsStickAttributeAvailable(category));
  const mergedStickAttribute = createMergedStickAttribute(stickAttributes, isMultiBrandSticksEnabled ?? false);
  const availableText = __("store_locator.popup.available_products.available");

  const allAttributes = [...attributes, ...mergedStickAttribute];

  return (
    <div className={styles.productAttributes}>
      {allAttributes.map((attribute) => {
        const categoryText = attribute.category
          ? __(`store_locator.popup.available_products.${attribute.category}`)
          : "";
        const keyText =
          attribute.key && attribute.key !== "Sticks"
            ? __(`store_locator.popup.available_products.${attribute.key}`)
            : "";

        return (
          <div key={attribute.key} className={styles.productAttributesItem}>
            <h3 className={styles.attributeName} data-testid="attributeName" key={attribute.category}>
              {availableText} {categoryText} {keyText}
            </h3>
            {attribute.category === "Stick" ? (
              <StoreLocatorStickAttributeValues values={attribute.values} isListView={isListView} />
            ) : (
              <StoreLocatorProductAttributeValues
                values={attribute.key === "ProductTypes" ? translateProductTypes(attribute.values) : attribute.values}
                isListView={isListView}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
